import { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { EnumHelperText, HelperText, Status, Switch, toastStore, Typography } from 'shared/ui'
import { MessageField } from 'widgets/MessageField'
import { type ChatbotActionMessageStore } from 'pages/chatbot/store/chatbotActionMessageStore'

import { ChatbotTextTooltip } from 'pages/chatbot/ui/ChatbotTooltip'
import { type IChatbotActionProps } from '../type'
import styles from './styles.module.scss'

export type IActionMessageProps = IChatbotActionProps<ChatbotActionMessageStore>

export const ActionMessage = observer(({ control, action, errors }: IActionMessageProps) => {
  const isDirty = useRef(false)
  const messageError = errors.getError(action)

  useEffect(() => {
    if (isDirty.current) errors.removeError(action)

    isDirty.current = true
  }, [control.message.messageText])

  useEffect(() => {
    if (isDirty.current && control.message.textLimitReached)
      toastStore.add({
        title: control.message.textLimit.message,
        type: 'error',
      })
  }, [control.message.textLimitReached])

  useEffect(() => control.syncMessage(), [])

  useEffect(() => {
    if (!!messageError) control.message.setHighlight('error')
    else control.message.clearHighlight()
  }, [messageError])

  const isAgentMessage = 'isAutoMessageType' in action

  return (
    <div className={styles.message}>
      {isAgentMessage && (
        <div className={styles.switchSection}>
          <div className={styles.switchLabelContainer}>
            <ChatbotTextTooltip
              tooltip='Conversations end with AI-generated messages by default, but you can opt to write your own'
              placement='bottom'
            >
              <Typography
                variant='text-input-field-r'
                color='tertiary'
                ariaLabel={'GeneratedByAILabel'}
              >
                Generated by AI
              </Typography>
            </ChatbotTextTooltip>
            <Status
              title={'Coming soon'}
              intent={'orange'}
              emphasis={'high'}
              size={'label'}
              className={styles.comingSoonStatus}
            />
          </div>
          <Switch
            value={action.isAutoMessageType}
            onChange={action.setIsAutoMessageType}
            disabled
          />
        </div>
      )}
      {(!isAgentMessage || (isAgentMessage && !action.isAutoMessageType)) && (
        <>
          <div className={styles.field}>
            <MessageField messageFieldStore={control.message} />
          </div>
          {!!messageError && (
            <HelperText
              icon={'alertTriangleSmall'}
              variant={EnumHelperText.Error}
              text={messageError}
            />
          )}
        </>
      )}
    </div>
  )
})
